/**
 * TechniqueModal Component
 *
 * Unified wizard modal for adding AND editing technique nodes in attack paths.
 * Steps:
 * 1. TechniqueSearchStep - Search/select MITRE technique or add custom
 * 2. TechniqueStatusStep - Select defense/detection status
 *
 * Features:
 * - Supports both 'add' and 'edit' modes via mode prop
 * - useOptimistic for instant node creation/update feedback
 * - Suspense boundaries for declarative loading
 * - ARIA live regions for accessibility
 * - Input sanitization for security
 */

import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useOptimistic,
} from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { Modal } from '@/components/Modal';
import type { WizardConfig, WizardStep } from '@/components/wizards';
import {
  useWizard,
  useWizardFooterConfig,
  WizardModalFooter,
  WizardProgressBar,
  WizardStepContainer,
} from '@/components/wizards';
import { useAttackState } from '@/sections/attacks/attackPaths/context';
import { useNodeUpdate } from '@/sections/attacks/attackPaths/hooks';
import type { CustomTechniqueNode } from '@/sections/attacks/attackPaths/types';

import { StepLoadingSkeleton } from './components';
import { TechniqueSearchStep, TechniqueStatusStep } from './steps';
import type { TechniqueWizardFormData } from './types';
import {
  createTechniqueFormData,
  techniqueFormDataFromNode,
  validateStatusStep,
  validateTechniqueStep,
} from './types';

/**
 * Modal mode type
 */
type NodeModalMode = 'add' | 'edit';

/**
 * Props for TechniqueModal component
 */
interface TechniqueModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Modal mode: 'add' for new nodes, 'edit' for existing nodes */
  mode: NodeModalMode;
  /** For edit mode: existing node data to pre-populate */
  initialNode?: CustomTechniqueNode;
  /** Optional callback when node is successfully created/updated */
  onComplete?: () => void;
}

/**
 * Wizard step definitions for technique creation/editing
 */
const techniqueWizardSteps: WizardStep<TechniqueWizardFormData>[] = [
  {
    id: 'technique-selection',
    order: 1,
    title: 'Select Technique',
    description: 'Choose a MITRE ATT&CK technique or add a custom one',
    validate: validateTechniqueStep,
  },
  {
    id: 'status-selection',
    order: 2,
    title: 'Select Status',
    description: 'Choose the defense/detection status for this technique',
    validate: validateStatusStep,
    isFinal: true,
  },
];

/**
 * Creates a CustomTechniqueNode from wizard form data.
 */
function createTechniqueNode(
  formData: TechniqueWizardFormData
): CustomTechniqueNode {
  // Determine the display name and ID
  const displayName = formData.customName || formData.techniqueName;
  const techniqueId = formData.techniqueId || '';

  return {
    id: uuidv4(),
    type: 'technique',
    position: { x: 0, y: 0 }, // Position will be set by layout
    data: {
      id: techniqueId,
      name: displayName,
      status: formData.status,
    },
  };
}

/**
 * Mode-specific labels for UI elements
 */
const MODE_LABELS = {
  add: {
    title: 'Add Technique Node',
    submit: 'Add',
    submitting: 'Adding...',
    successMessage: 'Technique node added to attack path',
    errorMessage: 'Failed to add technique node',
  },
  edit: {
    title: 'Edit Technique Node',
    submit: 'Save',
    submitting: 'Saving...',
    successMessage: 'Technique node updated',
    errorMessage: 'Failed to update technique node',
  },
} as const;

/**
 * TechniqueModal - Unified modal wizard for adding/editing technique nodes.
 *
 * Implements a 2-step flow:
 * 1. Technique selection (search MITRE ATT&CK or add custom)
 * 2. Status selection (defense/detection outcome)
 *
 * Uses React 19 patterns:
 * - useOptimistic for instant node addition/update with automatic rollback
 * - Suspense for declarative loading states
 *
 * @example
 * ```tsx
 * // Add mode
 * <TechniqueModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   mode="add"
 *   onComplete={() => toast.success('Technique added')}
 * />
 *
 * // Edit mode
 * <TechniqueModal
 *   open={editModalOpen}
 *   onClose={() => setEditModalOpen(false)}
 *   mode="edit"
 *   initialNode={existingNode}
 *   onComplete={() => toast.success('Technique updated')}
 * />
 * ```
 */
export function TechniqueModal({
  open,
  onClose,
  mode,
  initialNode,
  onComplete,
}: TechniqueModalProps) {
  const { nodes, addNode } = useAttackState();
  const { updateTechnique } = useNodeUpdate(initialNode?.id ?? '');

  const labels = MODE_LABELS[mode];

  // Compute initial data based on mode
  const initialData = useMemo(() => {
    if (mode === 'edit' && initialNode) {
      return techniqueFormDataFromNode(initialNode);
    }
    return createTechniqueFormData();
  }, [mode, initialNode]);

  // Optimistic state for instant UI feedback
  const [, addOptimisticNode] = useOptimistic(
    nodes,
    (state, newNode: CustomTechniqueNode) => [...state, newNode]
  );

  /**
   * Handle wizard submission - create or update the node
   */
  const handleSubmit = useCallback(
    async (formData: TechniqueWizardFormData) => {
      try {
        if (mode === 'edit' && initialNode) {
          // Edit mode: update existing node
          const displayName = formData.customName || formData.techniqueName;
          const techniqueId = formData.techniqueId || initialNode.data.id;
          updateTechnique(techniqueId, displayName, formData.status);
          toast.success(labels.successMessage);
        } else {
          // Add mode: create new node
          const newNode = createTechniqueNode(formData);
          addOptimisticNode(newNode);
          addNode(newNode);
          toast.success(labels.successMessage);
        }
        onComplete?.();
        onClose();
      } catch {
        toast.error(labels.errorMessage);
      }
    },
    [
      mode,
      initialNode,
      updateTechnique,
      addOptimisticNode,
      addNode,
      labels,
      onClose,
      onComplete,
    ]
  );

  // Configure wizard
  const wizardConfig: WizardConfig<TechniqueWizardFormData> = useMemo(
    () => ({
      steps: techniqueWizardSteps,
      initialData,
      onSubmit: handleSubmit,
    }),
    [initialData, handleSubmit]
  );

  const wizard = useWizard(wizardConfig);
  const { reset } = wizard;

  // Reset wizard to step 1 when modal opens
  // This ensures clean state after any close scenario (submit, cancel, escape, click outside)
  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  // Configure footer buttons
  const footerConfig = useWizardFooterConfig({
    canGoNext: wizard.navigation.canGoNext,
    canGoBack: wizard.navigation.canGoBack,
    isPending: wizard.isSubmitting,
    isLastStep: wizard.navigation.isLastStep,
    onNext: wizard.navigation.handleNext,
    onBack: wizard.navigation.handleBack,
    onCancel: () => {
      wizard.reset();
      onClose();
    },
    onSubmit: wizard.submit,
    labels: {
      next: 'Next',
      back: 'Back',
      cancel: 'Cancel',
      submit: labels.submit,
      submitting: labels.submitting,
    },
  });

  /**
   * Handle modal close with reset
   */
  const handleClose = () => {
    wizard.reset();
    onClose();
  };

  /**
   * Render the current step content
   */
  const renderStepContent = () => {
    switch (wizard.currentStepId) {
      case 'technique-selection':
        return (
          <TechniqueSearchStep
            formData={wizard.formData}
            setFormData={wizard.setFormData}
            preselectedKey={
              mode === 'edit' && !wizard.formData.customName
                ? wizard.formData.techniqueId
                : undefined
            }
          />
        );
      case 'status-selection':
        return (
          <TechniqueStatusStep
            formData={wizard.formData}
            setFormData={wizard.setFormData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={labels.title}
      hideFooter
      size="4xl"
      data-testid={`${mode}-technique-wizard-modal`}
    >
      {/* ARIA live region for step changes */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        Step {wizard.progress.currentDisplayStep} of{' '}
        {wizard.progress.totalDisplaySteps}: {wizard.progress.currentStepTitle}
      </div>

      {/* Progress bar */}
      <WizardProgressBar
        currentStep={wizard.progress.currentDisplayStep}
        totalSteps={wizard.progress.totalDisplaySteps}
        stepTitle={wizard.progress.currentStepTitle}
        percentage={wizard.progress.percentage}
      />

      {/* Step content with Suspense boundary */}
      <WizardStepContainer>
        <Suspense fallback={<StepLoadingSkeleton />}>
          {renderStepContent()}
        </Suspense>
      </WizardStepContainer>

      {/* Error display */}
      {wizard.submitError && (
        <div
          className="rounded-md bg-red-500/10 p-3 text-sm text-red-500"
          role="alert"
        >
          {wizard.submitError}
        </div>
      )}

      {/* Footer with navigation buttons */}
      <WizardModalFooter config={footerConfig} className="mt-0" />
    </Modal>
  );
}
